import * as React from "react";

import {
  TextArea,
  TextAreaChangeEvent,
  TextAreaProps,
} from "@progress/kendo-react-inputs";

import { FloatingLabel } from "@progress/kendo-react-labels";
import { StackLayout } from "@progress/kendo-react-layout";
import { CPText } from "shared/components/base";
import { getFieldError } from "shared/helpers";
import "./CPTextAreaStyles.scss";

export interface CPTextAreaProps extends Omit<
  TextAreaProps,
  "name" | "label" | "required" | "onChange" | "onBlur"
> {
  /** Unique field name; used as the `name` attribute and included in all change/blur events. */
  name: string;
  /** Floating label displayed above the textarea. */
  label?: string;
  /** Helper text shown below the textarea when there is no error. */
  helpText?: string;
  /** Error message displayed below the textarea in red; replaces `helpText` when set. */
  errorMsg?: string;
  /** Appends ` *` to the label to indicate the field is required. */
  required?: boolean;
  /** Prevents the user from resizing the textarea by dragging its handle. */
  disableResize?: boolean;
  /** Raw API error response `{ errors: [{ propertyName, errorMessage }] }`. Used with `name` to resolve a field-level error. */
  error?: any;
  /** Callback fired on every value change. Receives a normalised `CPTextAreaChangeEvent`. */
  onChange?: (event: CPTextAreaChangeEvent) => void;
  /** Callback fired when the textarea loses focus. Receives a normalised `CPTextAreaEvent`. */
  onBlur?: (event: CPTextAreaEvent) => void;
}

export interface CPTextAreaChangeEvent extends Omit<CPTextAreaEvent, "type"> {
  type: "CPTextAreaChangeEvent";
}

export interface CPTextAreaEvent {
  name: string;
  type: "CPTextAreaEvent";
  syntheticEvent: React.SyntheticEvent<HTMLElement, Event>;
  value: string;
  error: string | undefined;
  isValid: boolean;
}

const defaultErrorMsg = "This field is required.";

/**
 * Wrapper around the Kendo `TextArea` with floating label, validation display,
 * and normalised change/blur events that include the field `name`.
 *
 * Supports all standard textarea attributes (`rows`, `cols`, `maxLength`) plus
 * a `disableResize` convenience prop.
 *
 * @example
 * ```tsx
 * <CPTextArea
 *   name="notes"
 *   label="Notes"
 *   rows={4}
 *   maxLength={500}
 *   disableResize
 *   value={form.notes}
 *   onChange={handleChange}
 * />
 * ```
 */
const CPTextArea: React.FC<CPTextAreaProps> = (props) => {
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => {
    if (props.error != null) setDirty(false);
  }, [props.error]);

  const handleChange = (event: TextAreaChangeEvent) => {
    if (props.onChange) {
      setDirty(true);
      const value = (event.value as string) ?? "";
      const isValid = value != null && value !== "";

      props.onChange({
        name: props.name ?? "",
        type: "CPTextAreaChangeEvent",
        syntheticEvent: event.syntheticEvent,
        value,
        isValid,
        error: isValid ? undefined : defaultErrorMsg,
      });
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    if (props.onBlur) {
      const value = event.target.value ?? "";
      const isValid = value != null && value !== "";

      props.onBlur({
        name: props.name ?? "",
        type: "CPTextAreaEvent",
        syntheticEvent: event,
        value,
        isValid,
        error: isValid ? undefined : defaultErrorMsg,
      });
    }
  };

  const {
    name,
    label,
    helpText,
    errorMsg,
    required,
    disableResize,
    error,
    onChange: _onChange,
    onBlur: _onBlur,
    style,
    value,
    className,
    ...rest
  } = props;

  const resolvedClassName =
    [className, disableResize ? "cp-textarea-no-resize" : undefined]
      .filter(Boolean)
      .join(" ") || undefined;

  const resolvedError =
    errorMsg ?? getFieldError(dirty ? undefined : error, name);

  const resolvedStyle: React.CSSProperties = {
    ...style,
  };

  const renderTextArea = () => (
    <TextArea
      {...rest}
      id={name}
      name={name}
      value={(value as string) ?? ""}
      style={resolvedStyle}
      className={resolvedClassName}
      size={props.size ?? "small"}
      onChange={handleChange}
      onBlur={handleBlur as any}
    />
  );

  return (
    <StackLayout orientation="vertical" gap={5}>
      {label != null ? (
        <FloatingLabel
          label={required ? `${label} *` : label}
          editorValue={(value as string) ?? ""}
          editorId={name}
        >
          {renderTextArea()}
        </FloatingLabel>
      ) : (
        renderTextArea()
      )}
      {resolvedError != null || helpText != null ? (
        <CPText color={resolvedError ? "error" : "primary"}>
          {resolvedError ?? helpText}
        </CPText>
      ) : null}
    </StackLayout>
  );
};

export default CPTextArea;
